import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { connect } from "react-redux";
import type { ReduxState } from "@/components/redux/reducers";
import { History, Location } from "history";
import type { match } from "react-router";
import { Link } from "react-router-dom";
import { FormattedMessage } from "react-intl";

import type { ICategory } from "@/lib/backend/manufacturing2.generated.types";

import { getBackend } from "@/lib/backend/Backend2";
import * as Auth from "@/lib/Auth";
import * as Redirect from "@/components/URLHandler/Redirect";

import ErrorComponent from "@/components/ErrorComponent";
import Loader from "@/components/Loader";
import CategoriesList from "./CategoriesList";
import CategoryDetails from "./CategoryDetails";
import { translate } from "@/components/IntlProviderWrapper";
import { ResourcesLayout } from "@/components/MainContent";

const CATEGORIES_BASE_URL = "/digital-twin/resources/categories";
const CATEGORY_CREATE_URL = "/digital-twin/resources/category/create";

const getCategoryUrl = (categoryId: number): string => {
    return `${CATEGORIES_BASE_URL}/${categoryId}`;
};

/**
 * Sort categories by ID in ascending order
 */
const sortCategories = (categories: ICategory[]): ICategory[] => {
    return categories.sort((a, b) => a.id - b.id);
};

/**
 * Filter categories based on search criteria (name or code)
 */
const filterItems = (items: ICategory[], search_value: string): ICategory[] => {
    const filter = search_value || "";
    if (!filter) return items;

    return items.filter(item => {
        const needle = filter.toLowerCase();
        return item.name.toLowerCase().indexOf(needle) > -1 || item.code.toLowerCase().indexOf(needle) > -1;
    });
};

/**
 * Select item from filtered categories based on content filter
 */
const selectItemFromFiltered = (
    content_filter: string,
    category_items: ICategory[],
    items_list_ref: any
): ICategory | null => {
    if (!category_items.length) return null;

    for (let item of category_items) {
        if (items_list_ref.current && items_list_ref.current.includes(content_filter, item)) {
            return item;
        }
    }
    return null;
};

type OwnProps = {
    match: match<{ id?: string }>;
    history: History;
    location: Location;
};
type Props = OwnProps & ReduxProps;

/**
 * Displaying categories list
 */
const Categories = (props: Props) => {
    const { match, history, resource_edit_filters } = props;
    const [items, setItems] = useState<ICategory[]>([]);
    const [selected, setSelected] = useState<ICategory | null>(null);
    const [error, setError] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState<string>(resource_edit_filters.category_title_filter || "");

    const items_list_ref = useRef<CategoriesList | null>(null);

    const loadComponent = useCallback(async () => {
        setIsLoading(true);
        setError("");
        try {
            const res = await getBackend().manufacturing.getCategories({ include_items: true });
            const sorted_categories = sortCategories(res.categories);
            setItems(sorted_categories);
        } catch (err) {
            setError(translate("common.error", "An error occurred while loading categories"));
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Load categories only once on mount
    useEffect(() => {
        loadComponent();
    }, [loadComponent]);

    // Handle URL changes and select appropriate category
    useEffect(() => {
        if (items.length === 0) {
            setSelected(null);
            return;
        }

        const categoryId = match.params.id ? parseInt(match.params.id, 10) : null;
        const selectedCategory = items.find(category => category.id === categoryId) || items[0];
        setSelected(selectedCategory);

        if (selectedCategory.id !== categoryId) {
            history.replace(getCategoryUrl(selectedCategory.id));
        }
    }, [match.params.id, items, history]);

    const handleUpdateCategory = useCallback(
        async (category: ICategory) => {
            const res = await getBackend().manufacturing.getCategory({
                category_id: category.id,
            });

            setItems(items.map(item => (item.id === category.id ? res.category : item)));
            setSelected(res.category);

            // Ensure URL is updated after category update
            history.replace(getCategoryUrl(res.category.id));
        },
        [items, history]
    );

    // Sync local search value with Redux state when it changes externally
    useEffect(() => {
        const redux_filter_value = resource_edit_filters.category_title_filter || "";
        if (redux_filter_value !== searchValue) {
            setSearchValue(redux_filter_value);
        }
    }, [resource_edit_filters.category_title_filter]);

    const selectItem = useCallback(
        (contentFilter: string, category_items: ICategory[] = items): ICategory | null => {
            return selectItemFromFiltered(contentFilter, category_items, items_list_ref);
        },
        [items]
    );

    /**
     * Handling change in search input - uses local state to prevent cursor jumping
     */
    const handleSearchInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            try {
                const value = event.currentTarget.value;
                const selectedCategory = selectItem(value);

                setSearchValue(value);
                setSelected(selectedCategory);

                // Update URL with selected category ID if one is found
                const baseUrl = selectedCategory ? getCategoryUrl(selectedCategory.id) : CATEGORIES_BASE_URL + "/";

                Redirect.resourceEditFilters(history, baseUrl, {
                    ...resource_edit_filters,
                    category_title_filter: value,
                });
            } catch (err: unknown) {
                setError((err as Error).message || "An error occurred while handling search input change");
            }
        },
        [selectItem, history, resource_edit_filters]
    );

    /**
     * Handle `click` events for category items
     */
    const handleItemClick = useCallback(
        (item: ICategory): void => {
            setSelected(item);
            // Update URL with selected category ID
            history.push(getCategoryUrl(item.id));
        },
        [history]
    );

    const handleDeleteCategory = useCallback(async () => {
        // After deletion, reload and select first available category
        await loadComponent();
    }, [loadComponent]);

    const can_edit = useMemo(() => Auth.isInElevatedRole(), []);

    const filtered_items = useMemo(() => {
        return filterItems(items, searchValue);
    }, [items, searchValue]);

    return (
        <ResourcesLayout>
            <ResourcesLayout.Main>
                <ResourcesLayout.List>
                    <ResourcesLayout.Search>
                        <div className="form-group form-group-stretch">
                            <input
                                type="text"
                                className="form-control search_bar"
                                placeholder={translate("common.search", "Search") + " ..."}
                                name="title_filter"
                                value={searchValue}
                                onChange={handleSearchInputChange}
                                aria-label={translate("common.search", "Search")}
                                autoComplete="off"
                            />
                        </div>
                        {can_edit && (
                            <div className="form-group">
                                <Link
                                    to={{
                                        pathname: CATEGORY_CREATE_URL,
                                        state: {
                                            categories_codes: items.map(item => item.code),
                                        },
                                    }}
                                    className="btn btn-inv-primary btn-icon btn-icon-add"
                                    title={translate("Categories.create_category", "Create category")}
                                >
                                    <span className="sr-only">
                                        <FormattedMessage
                                            id="Categories.create_category"
                                            defaultMessage="Create category"
                                        />
                                    </span>
                                </Link>
                            </div>
                        )}
                    </ResourcesLayout.Search>
                    <CategoriesList
                        ref={items_list_ref}
                        items={filtered_items}
                        selected={selected}
                        handleItemClick={handleItemClick}
                        filter={searchValue}
                        plantFilter=""
                    />
                </ResourcesLayout.List>
                <ResourcesLayout.Content>
                    {error && <ErrorComponent msg={error} type="error" />}
                    {isLoading && <Loader />}
                    {!isLoading && !error && items.length === 0 && (
                        <div className="text-center text-muted py-4">
                            <p>{translate("Categories.no_categories", "No categories found")}</p>
                        </div>
                    )}
                    {!error && (
                        <CategoryDetails
                            selected={selected}
                            onCategoryDelete={handleDeleteCategory}
                            onCategoryUpdate={handleUpdateCategory}
                        />
                    )}
                </ResourcesLayout.Content>
            </ResourcesLayout.Main>
        </ResourcesLayout>
    );
};

const MemoizedCategories = React.memo(Categories);

const mapState = (state: ReduxState) => {
    return { resource_edit_filters: state.resource_edit_filters };
};

type ReduxProps = ReturnType<typeof mapState>;
export default connect<ReturnType<typeof mapState>, {}, OwnProps, ReduxState>(mapState)(MemoizedCategories);
